import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Spinner, Alert, Container, Badge } from 'react-bootstrap';
import SignalTable from '../components/SignalTable';
import ActiveTradesComponent from '../components/ActiveTrades';
import { signalsApi, activeTradesApi } from '../services/api';

const ActiveTradesPage = () => {
  const [activeTrades, setActiveTrades] = useState([]);
  const [signals, setSignals] = useState([]);
  const [loadingTrades, setLoadingTrades] = useState(true);
  const [loadingSignals, setLoadingSignals] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Selected symbol for filtering
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  
  // Filtering state
  const [filters, setFilters] = useState({
    searchTerm: '',
    signalType: 'all',
    timeframe: 'all',
    sortField: 'timestamp',
    sortDirection: 'desc'
  });
  
  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);
  
  // Function to fetch active trades
  const fetchActiveTrades = useCallback(async () => {
    try {
      setLoadingTrades(true);
      
      const response = await activeTradesApi.getAll();
      setActiveTrades(response.activeTrades || []);
    } catch (err) {
      console.error('Error fetching active trades:', err);
      setError('Failed to load active trades. Please try again later.');
    } finally {
      setLoadingTrades(false);
    }
  }, []); // No dependencies as it doesn't rely on changing state
  
  // Function to fetch signals for active trades
  const fetchActiveSignals = useCallback(async () => {
    try {
      setLoadingSignals(true);
      
      // Create a new filter object with the selected symbol if applicable
      const apiFilters = {...filters};
      if (selectedSymbol) {
        apiFilters.searchTerm = selectedSymbol;
      }
      
      const response = await signalsApi.getActiveTradeSignals(apiFilters);
      setSignals(response.signals || []);
    } catch (err) {
      console.error('Error fetching active trade signals:', err);
      setError('Failed to load signals. Please try again later.');
    } finally {
      setLoadingSignals(false);
    }
  }, [filters, selectedSymbol]); // Depends on filters AND selectedSymbol
  
  // Fetch active trades and signals on component mount
  useEffect(() => {
    fetchActiveTrades();
    fetchActiveSignals();
  }, [fetchActiveTrades, fetchActiveSignals]);
  
  // Function to add a new active trade
  const handleAddTrade = async (symbol, notes) => {
    try {
      await activeTradesApi.add(symbol, notes);
      
      // Refresh data
      await fetchActiveTrades();
      await fetchActiveSignals();
      
      // Show success message
      setSuccessMessage(`Added ${symbol.toUpperCase()} to watchlist successfully.`);
    } catch (err) {
      console.error('Error adding active trade:', err);
      throw err;
    }
  };
  
  // Function to remove an active trade
  const handleRemoveTrade = async (symbol) => {
    try {
      await activeTradesApi.remove(symbol);
      
      // If the removed symbol was selected, clear the selection
      if (selectedSymbol === symbol) {
        setSelectedSymbol(null);
      }
      
      // Refresh data
      await fetchActiveTrades();
      await fetchActiveSignals();
      
      // Show success message
      setSuccessMessage(`Removed ${symbol.toUpperCase()} from watchlist successfully.`);
    } catch (err) {
      console.error('Error removing active trade:', err);
      throw err;
    }
  };
  
  // Handle filter changes from SignalTable
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  // Handle clicking on a symbol in the watchlist
  const handleSymbolClick = (symbol) => {
    if (selectedSymbol === symbol) {
      // If already selected, clear the filter
      setSelectedSymbol(null);
    } else {
      // Otherwise, set the filter
      setSelectedSymbol(symbol);
    }
  };
  
  // Handle refresh
  const handleRefresh = () => {
    fetchActiveTrades();
    fetchActiveSignals();
  };
  
  // Handle signals deletion
  const handleSignalsDeleted = (result) => {
    // Show success message
    if (result.results) {
      // For batch delete operations
      const { deleted, failed } = result.results;
      if (failed && failed.length > 0) {
        setSuccessMessage(`Successfully deleted ${deleted.length} signals. Failed to delete ${failed.length} signals.`);
      } else {
        setSuccessMessage(`Successfully deleted ${deleted.length} signals.`);
      }
    } else {
      // For single delete operations
      setSuccessMessage('Signal deleted successfully.');
    }
    
    // Refresh signals
    fetchActiveSignals();
  };
  
  return (
    <div className="active-trades-page">
      <Container fluid>
        <Row>
          {/* Sidebar for watchlist */}
          <Col md={3} lg={2} className="watchlist-sidebar mb-4">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Header className="bg-dark text-white">
                <h5 className="mb-0">Current Watchlist</h5>
              </Card.Header>
              <Card.Body className="p-0">
                {loadingTrades ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading...
                  </div>
                ) : activeTrades.length === 0 ? (
                  <div className="p-3 text-center text-muted">
                    No symbols in watchlist
                  </div>
                ) : (
                  <div className="watchlist-symbols">
                    {activeTrades.map(trade => (
                      <div
                        key={trade.symbol}
                        className={`watchlist-item ${selectedSymbol === trade.symbol ? 'active' : ''}`}
                        onClick={() => handleSymbolClick(trade.symbol)}
                      >
                        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                          <span className="symbol">{trade.symbol.toUpperCase()}</span>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveTrade(trade.symbol);
                            }}
                          >
                            <i className="bi bi-x"></i>
                          </Button>
                        </div>
                        {trade.notes && (
                          <div className="notes small text-muted p-2 bg-light">
                            {trade.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Main content */}
          <Col md={9} lg={10}>
            <Row className="mb-4">
              <Col>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="bg-primary text-white">
                    <h4 className="mb-0">Add Symbol to Watchlist</h4>
                  </Card.Header>
                  <Card.Body>
                    {/* Success message for operations */}
                    {successMessage && (
                      <Alert 
                        variant="success" 
                        onClose={() => setSuccessMessage(null)} 
                        dismissible
                        className="mb-4"
                      >
                        <i className="bi bi-check-circle-fill me-2"></i>
                        {successMessage}
                      </Alert>
                    )}
                    
                    <ActiveTradesComponent
                      activeTrades={[]} // Don't pass the full list since we display it in the sidebar
                      loading={false}
                      onAddTrade={handleAddTrade}
                      onRemoveTrade={handleRemoveTrade}
                      compact={true}
                      hideList={true}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <Row>
              <Col>
                <Card className="border-0 shadow-sm">
                  <Card.Header className="d-flex justify-content-between align-items-center bg-dark text-white">
                    <div>
                      <h4 className="mb-0">
                        Active Trade Signals
                        {selectedSymbol && (
                          <Badge bg="primary" className="ms-2">
                            {selectedSymbol.toUpperCase()}
                            <Button 
                              variant="link" 
                              className="text-white ms-2 p-0" 
                              onClick={() => setSelectedSymbol(null)}
                              style={{ fontSize: '0.8rem', textDecoration: 'none' }}
                            >
                              <i className="bi bi-x-circle"></i>
                            </Button>
                          </Badge>
                        )}
                      </h4>
                    </div>
                    <Button 
                      variant="outline-light" 
                      onClick={handleRefresh} 
                      disabled={loadingSignals}
                    >
                      {loadingSignals ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <i className="bi bi-arrow-clockwise"></i>
                      )}{' '}
                      Refresh
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {error ? (
                      <div className="text-center text-danger my-4">
                        <p>{error}</p>
                        <Button variant="primary" onClick={handleRefresh}>
                          Try Again
                        </Button>
                      </div>
                    ) : (
                      <SignalTable 
                        signals={signals} 
                        loading={loadingSignals && !signals.length} 
                        isActiveTrades={true}
                        onFilterChange={handleFilterChange}
                        initialFilters={filters}
                        onSignalsDeleted={handleSignalsDeleted}
                      />
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ActiveTradesPage;